import React from 'react'
import {
  LazyLoadImage,
  trackWindowScroll,
} from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/opacity.css'
import {
  AdvancedImage,
  lazyload,
  placeholder,
  responsive,
} from '@cloudinary/react'
import { Cloudinary } from '@cloudinary/url-gen'
// fit is renamed to cldFit to avoid a naming collision with Jasmine's global
// fit() function (used for focused tests), which triggers the jasmine/no-focused-tests lint rule.
import { fill, fit as cldFit } from '@cloudinary/url-gen/actions/resize'
import { quality } from '@cloudinary/url-gen/actions/delivery'
import { format } from '@cloudinary/url-gen/actions/delivery'
import { auto as autoQuality } from '@cloudinary/url-gen/qualifiers/quality'
import { auto as autoFormat } from '@cloudinary/url-gen/qualifiers/format'
import { combineClasses, getCloudinaryPublicId } from '~/util'
import { Loader } from '../loader'
import type { MediaItem } from '~/service'
import { env } from '~/service'

import AlertIcon from '~/assets/icons/alert-triangle.svg?react'
import styles from './Image.module.scss'

// Re-exported for ease of use.
export { trackWindowScroll }

export interface ImageProps {
  /**
   * The media item defining the image to load. It is better to pass the
   * media item when possible because this gives better results for
   * Cloudinary-based responsive images.
   */
  mediaItem?: MediaItem
  /**
   * The image src if not using the media item prop. If passing both,
   * the media item takes precedent.
   */
  src?: string
  /**
   * The name of the PowerPro Cloudinary account (the cloud name in
   * Cloudinary terminology).
   */
  cloudinaryCloudName?: string
  /** Whether to show a loader while the image is fetching. */
  loader?: boolean
  /**
   * Whether to defer loading the image until it enters the viewport.
   * Only use this for images guaranteed to be out of the viewport on
   * first load.
   */
  lazy?: boolean
  /**
   * Turning this on will request the image at the dimensions of its
   * parent container. Reduces bandwidth on mobile. Works with
   * Cloudinary images only.
   */
  responsive?: boolean
  /**
   * The CSS object-fit value. Using this prop also sets the necessary
   * styles on the wrapping elements for consistent results.
   */
  fit?: string
  /**
   * Sets the CSS display property on the image and its wrapping
   * elements for consistent layout results.
   */
  display?: string
  /**
   * Specific width for the image element. Also sets wrapping element
   * styles so the image behaves as expected.
   */
  width?: number | string
  /**
   * Specific height for the image element. Also sets wrapping element
   * styles so the image behaves as expected.
   */
  height?: number | string
  /** Removes the background color behind the image. */
  transparent?: boolean
  alt?: string
  className?: string
  style?: React.CSSProperties
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void
  /** Called when the image has finished loading. */
  onLoad?: () => void
  [key: string]: unknown
}

export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  // ref forwarding is only supported for non-Cloudinary images via LazyLoadImage.
  // AdvancedImage is a class component and does not expose the underlying img ref.
  (
    {
      className,
      transparent = false,
      loader = true,
      lazy = false,
      style,
      alt,
      fit: fitProp = 'cover',
      mediaItem,
      src: srcProp,
      cloudinaryCloudName = env.cloudinaryCloudName,
      width = '100%',
      height = '100%',
      responsive: responsiveProp = false,
      display,
      onError,
      onLoad = () => {},
      ...props
    },
    _ref
  ) => {
    const src = srcProp ?? mediaItem?.url ?? mediaItem?.previewUrl

    const [error, setError] = React.useState(false)
    const [cldError, setCldError] = React.useState(false)
    const [loading, setLoading] = React.useState(false)

    const cloudinaryPublicId = getCloudinaryPublicId(
      mediaItem,
      src,
      cloudinaryCloudName
    )

    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      // Cloudinary failed — fall back to native image loading before giving up.
      setCldError(true)
      if (onError) onError(e)
    }

    const handleLazyLoadError = () => {
      setError(true)
      if (onError) onError({} as React.SyntheticEvent<HTMLImageElement>)
    }

    // Build the Cloudinary image object using the new SDK.
    const cldImage = React.useMemo(() => {
      if (!cloudinaryPublicId) return null

      const cld = new Cloudinary({ cloud: { cloudName: cloudinaryCloudName } })
      const img = cld.image(cloudinaryPublicId)

      // Resize transformation.
      // For lazy+responsive images, c_fit is added here and the responsive()
      // plugin will append the measured width at runtime (e.g. c_scale,w_932).
      // For non-lazy images, skip the resize and let CSS handle sizing — this
      // avoids the double request that the responsive() plugin causes on
      // above-fold images.
      if (responsiveProp && lazy) {
        img.resize(cldFit())
      } else if (!responsiveProp) {
        const pixelWidth = typeof width === 'number' ? width : undefined
        const pixelHeight = typeof height === 'number' ? height : undefined
        if (pixelWidth || pixelHeight) {
          const resizeAction = fill()
          if (pixelWidth) resizeAction.width(pixelWidth)
          if (pixelHeight) resizeAction.height(pixelHeight)
          img.resize(resizeAction)
        }
      }

      // Always apply format and quality optimizations.
      img.delivery(quality(autoQuality()))
      img.delivery(format(autoFormat()))

      return img
    }, [
      cloudinaryPublicId,
      cloudinaryCloudName,
      responsiveProp,
      lazy,
      width,
      height,
    ])

    // Build the plugin list for AdvancedImage.
    // responsive() and placeholder() are only used for lazy (off-screen) images.
    // Above-fold images skip these plugins to avoid multiple sequential Cloudinary
    // requests which hurt LCP on slow connections.
    const cldPlugins = React.useMemo(() => {
      const plugins = []
      if (lazy) {
        if (responsiveProp)
          plugins.push(responsive({ steps: [400, 600, 800, 1200] }))
        plugins.push(lazyload())
        plugins.push(placeholder({ mode: 'blur' }))
      }
      return plugins
    }, [responsiveProp, lazy])

    const containerStyle: React.CSSProperties = {
      ...style,
      display,
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
    }

    const imageStyle: React.CSSProperties = {
      display: 'block',
      objectFit: fitProp as React.CSSProperties['objectFit'],
      width,
      height,
    }

    return (
      <span
        className={combineClasses(
          styles.Image,
          className,
          transparent ? styles.transparent : null
        )}
        style={containerStyle}
      >
        {loader && loading && !error && (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <Loader
            {...({ 'data-testid': 'Loader' } as any)}
            className={combineClasses(styles.Loader, 'loader')}
          />
        )}
        {error && (
          <AlertIcon
            aria-label={alt}
            className={combineClasses(styles.ErrorIcon, 'error')}
            data-testid="error"
          >
            <title>{alt}</title>
          </AlertIcon>
        )}
        {cldImage && src && !error && !cldError && (
          // AdvancedImage is a class component — ref forwarding targets the
          // non-Cloudinary path only. data-* props are accepted via [x: string]: any.
          <AdvancedImage
            data-testid="content"
            data-type="cloudinary"
            className={combineClasses(styles.cloudinaryImage, 'image')}
            cldImg={cldImage}
            plugins={cldPlugins}
            alt={alt}
            style={imageStyle}
            onLoad={onLoad}
            onError={handleError}
            {...props}
          />
        )}
        {(!cldImage || cldError) && src && !error && (
          <LazyLoadImage
            {...({ 'data-testid': 'content', 'data-type': 'native' } as Record<
              string,
              string
            >)}
            className={combineClasses(styles.lazyImage, 'image')}
            style={{
              display: 'block',
              objectFit: fitProp as React.CSSProperties['objectFit'],
              width: '100%',
              height: '100%',
            }}
            wrapperClassName={combineClasses(
              styles.wrapperClass,
              'image-wrapper'
            )}
            visibleByDefault={!lazy}
            wrapperProps={
              {
                style: { display: 'inline' },
                'data-testid': 'imageWrapper',
              } as React.HTMLAttributes<HTMLSpanElement>
            }
            alt={alt}
            src={src}
            {...props}
            beforeLoad={() => setLoading(true)}
            onLoad={() => {
              setLoading(false)
              onLoad()
            }}
            onError={handleLazyLoadError}
          />
        )}
      </span>
    )
  }
)

Image.displayName = 'Image'
